import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import axiosInstance from "../../axiosInstance";
import GeneralTable from "../../components/Table/GeneralTable";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import LaunchedTemplateReviewForm from "./LaunchedTemplateReviewForm";
import "./LaunchedTemplates.scss";

// launchTemplateStatus only ever comes back as "Launched" for items in this
// list, so it's fixed rather than a filter. versionType is a 2-way toggle.
const LAUNCH_STATUS = "LAUNCHED";
const VERSION_TYPES = ["NEW", "UPDATED"];

const renderNotificationBadges = (notificationTypes) => {
  if (!notificationTypes || notificationTypes.length === 0) return "N/A";
  return (
    <div className="notification-badges">
      {notificationTypes.map((notif) => (
        <span
          key={notif.notificationTypeId}
          className={`notification-badge ${notif.notificationType?.toLowerCase()}`}
        >
          {notif.notificationType?.replace("_", "-")}
        </span>
      ))}
    </div>
  );
};

const LaunchedTemplates = () => {
  const navigate = useNavigate();

  const [activeType, setActiveType] = useState("event"); // "event" | "cronjob"
  const [versionType, setVersionType] = useState("New");
  const [page, setPage] = useState(0);
  const [size] = useState(10);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [reviewingItem, setReviewingItem] = useState(null);

  const isEvent = activeType === "event";

  const fetchLaunched = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const endpoint = isEvent
        ? `${process.env.REACT_APP_API_BASE_URL}/scheduled-events/launched-notifications`
        : `${process.env.REACT_APP_API_BASE_URL}/scheduled-cron-jobs/launched-automations`;

      const { data } = await axiosInstance.get(endpoint, {
        params: { page, size, status: LAUNCH_STATUS, versionType },
      });

      setItems(data.data || []);
    } catch (err) {
      console.error("Error fetching launched templates:", err);
      setError("Failed to load launched templates.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [isEvent, page, size, versionType]);

  useEffect(() => {
    fetchLaunched();
  }, [fetchLaunched]);

  const handleSaved = () => {
    setReviewingItem(null);
    fetchLaunched();
  };

  const switchType = (type) => {
    setActiveType(type);
    setPage(0);
  };

  const switchVersion = (v) => {
    setVersionType(v);
    setPage(0);
  };

  const eventColumns = [
    { header: "Task Name", accessor: "taskName" },
    { header: "Notification Type", accessor: "eventType" },
    { header: "Trigger Types", accessor: "notificationTypes" },
    { header: "From Email", accessor: "fromEmailSource" },
    { header: "To Email", accessor: "toEmailSourceFrom" },
    { header: "Actions", accessor: "actions" },
  ];

  const cronjobColumns = [
    { header: "Task Name", accessor: "taskName" },
    { header: "Automation Type", accessor: "cronJobType" },
    { header: "Notification Types", accessor: "notificationTypes" },
    { header: "From Email", accessor: "fromEmailSource" },
    { header: "To Email", accessor: "toEmailSourceFrom" },
    { header: "Actions", accessor: "actions" },
  ];

  // Only one action is ever shown per row:
  // alreadyUsed === true  -> "Save"   (direct save)
  // alreadyUsed === false -> "Update" (opens duplicate/overwrite confirm in the form)
  const transformedData = items.map((item) => ({
    ...item,
    notificationTypes: renderNotificationBadges(item.notificationTypes),
    actions: (
      <div className="action-buttons">
        <button
          className="review-button"
          onClick={() => setReviewingItem(item)}
        >
          {item.alreadyUsed ? "Save" : "Update"}
        </button>
      </div>
    ),
  }));

  return (
    <div className="launched-templates-container">
      <div className="launched-templates-header">
        <button
          className="back-button"
          onClick={() => navigate("/email-automation")}
          aria-label="Back to Email Automation"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="display6">Launched Templates</h1>
      </div>

      <div className="launched-templates-tabs">
        <button
          className={`module-tab ${isEvent ? "active" : ""}`}
          onClick={() => switchType("event")}
        >
          Launched Notifications
        </button>
        <button
          className={`module-tab ${!isEvent ? "active" : ""}`}
          onClick={() => switchType("cronjob")}
        >
          Launched Automations
        </button>
      </div>

      <div className="launched-templates-filters">
        {VERSION_TYPES.map((v) => (
          <button
            key={v}
            className={`version-toggle ${versionType === v ? "active" : ""}`}
            onClick={() => switchVersion(v)}
          >
            {v}
          </button>
        ))}
      </div>

      {loading ? (
        <LoadingScreen />
      ) : error ? (
        <p className="error-message">{error}</p>
      ) : items.length === 0 ? (
        <p className="empty-hint">No launched templates match these filters.</p>
      ) : (
        <GeneralTable
          columns={isEvent ? eventColumns : cronjobColumns}
          data={transformedData}
          title={isEvent ? "Launched Notifications" : "Launched Automations"}
        />
      )}

      <div className="launched-templates-pagination">
        <button
          disabled={page === 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
        >
          Previous
        </button>
        <span>Page {page + 1}</span>
        <button
          disabled={items.length < size}
          onClick={() => setPage((p) => p + 1)}
        >
          Next
        </button>
      </div>

      {reviewingItem && (
        <LaunchedTemplateReviewForm
          item={reviewingItem}
          type={activeType}
          onClose={() => setReviewingItem(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

export default LaunchedTemplates;
